import { Menu } from "@/components/Menu";
import { Tooltip } from "@/components/Tooltip";

import styles from "./AccountMenu.module.css";

/**
 * The person's initials, as on any account key: the first letter of the
 * first and of the last word of the name — „Denis Beispiel" → „DB", „Chiara"
 * → „C". Hyphens part words as spaces do („Anna-Lena Roth" → „AR").
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/[\s-]+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = words[0]!.charAt(0);
  const last = words.length > 1 ? words[words.length - 1]!.charAt(0) : "";
  return `${first}${last}`.toLocaleUpperCase("de");
}

/**
 * The account in the masthead (UX-Gutachten 2, B-5): the name in tracked
 * capitals read like a third place beside „Start" and „Vorgänge", and
 * „Abmelden" beside it was a fifth type face at the masthead's right end.
 * Now one round key of 32 px with the initials, its tooltip the name — its
 * name starts with the initials it shows („DB, Konto: Denis Beispiel", WCAG
 * 2.5.3) —, and
 * behind it the app's one menu (`Menu`) — the name on top, „Abmelden" under
 * it, which does what the button did (`onLogout`, after the menu has closed
 * and handed the focus back).
 *
 * Its colours are where it stands (`inherit`, `currentcolor`): the masthead's
 * one type rule for its right end sets them (AppShell.module.css).
 */
export function AccountMenu({ name, onLogout }: { name: string; onLogout: () => void }) {
  return (
    <Menu
      label={`Konto: ${name}`}
      heading={name}
      items={[{ key: "logout", label: "Abmelden", onSelect: onLogout }]}
      trigger={({ ref, ...props }) => (
        <Tooltip text={name}>
          <button
            ref={ref}
            type="button"
            className={styles.key}
            aria-label={`${initialsOf(name)}, Konto: ${name}`}
            {...props}
          >
            <span aria-hidden="true">{initialsOf(name)}</span>
          </button>
        </Tooltip>
      )}
    />
  );
}
